/**
 * Auth Service
 * 
 * Bu servis, kimlik doğrulama ile ilgili işlevleri merkezileştirip 
 * yönetir ve API istekleri için axios interceptor'lar sağlar.
 */

import axios from 'axios';
import useStore from '../store/useStore';
import { jwtDecode } from 'jwt-decode';

import clientConfig from '../helpers/clientConfig';
import logger from '../utils/logger';
import keycloak, { ensureKcToken, getKcToken } from '../keycloak';

// API için baseURL ayarı — Cross-origin sorunlarını önlemek için relative path (/api/) zorunludur.
// Ingress kuralları portal domain'i üzerindeki /api isteklerini gateway'e yönlendirir.
let API_URL = '/api/';

if (process.env.NODE_ENV === 'development') {
    console.log('[AuthService] Initialized with API_URL:', API_URL);
}

// Base URL'in sonunda /api/ olduğundan emin ol (Standardizasyon)
if (API_URL) {
    if (!API_URL.endsWith('/api') && !API_URL.endsWith('/api/')) {
        API_URL = API_URL.endsWith('/') ? `${API_URL}api` : `${API_URL}/api`;
    }
    if (!API_URL.endsWith('/')) {
        API_URL = `${API_URL}/`;
    }
}

/** config üzerinden subdomain döner (tenant bilgisi için) */
function getTenantSubdomain() {
    if (typeof window === 'undefined' || !window.location?.hostname) return null;
    const h = window.location.hostname;
    
    // The bare application host is not a tenant. Tenant UI has one canonical `{tenant}.app.*` shape.
    const agentSuffix = clientConfig.REACT_APP_AGENT_SUFFIX || '.app.dev.arvis.com.tr';
    const appRoot = agentSuffix.replace(/^\./, '');
    if (h === appRoot) return null;

    let sub = null;
    if (agentSuffix && h.endsWith(agentSuffix)) {
        const candidate = h.slice(0, -agentSuffix.length);
        if (candidate && !candidate.includes('.') && /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/.test(candidate)) {
            sub = candidate;
        }
    }

    // Sistem kelimelerini ayıkla
    const reserved = ['app', 'admin', 'www', 'api', 'dev'];
    return (sub && !reserved.includes(sub)) ? sub : null;
}

// Axios instance — x-api-key SADECE SDK'larda kullanılır, panel/login'de gönderilmez
// withCredentials: refresh akışı artık httpOnly cookie'ye (arvis_refresh, Domain=.dev.arvis.com.tr)
// dayanıyor — tarayıcının bunu otomatik göndermesi için her istekte zorunlu.
const api = axios.create({
    baseURL: API_URL,
    timeout: 30000, // 30 saniye
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json'
    }
});

// Token'ın yenilenme sürecinde olup olmadığını kontrol eden değişken
let isRefreshing = false;
// Token yenileme bekleyen isteklerin kuyruğu
let refreshSubscribers = [];

// Bekleyen istekleri onaylamak için
const onTokenRefreshed = (token) => {
    refreshSubscribers.forEach(callback => callback(token));

    refreshSubscribers = [];
};

// Yeni bir istek eklemek için
const addRefreshSubscriber = (callback) => {
    refreshSubscribers.push(callback);
};

// Son token yenileme çağrısının zamanını tut
let lastTokenRefresh = 0;
// Minimum token yenileme aralığı (5 saniye)
const MIN_REFRESH_INTERVAL = 5000;
// Token süresinin % kaçı kaldığında yenileme başlatılacak
const TOKEN_REFRESH_THRESHOLD = 0.5; // %80 yerine %50 süre kullanıldığında yenile

// Memory'deki (Zustand) access token'ın süresini kontrol eden ve süresi dolmak üzere ise
// httpOnly refresh cookie'yi kullanarak otomatik yenileyen fonksiyon. Token artık
// localStorage'da değil — sadece getState().token (memory) üzerinden okunur.
const checkAndRefreshToken = async () => {
    if (!getState) return false;
    const token = getState().token;
    if (!token) {
        logger.debug('checkAndRefreshToken: memory token bulunamadı.');
        return false;
    }

    try {
        const decoded = jwtDecode(token);
        const currentTime = Date.now() / 1000;
        const expiryTime = decoded.exp;
        const totalTime = expiryTime - decoded.iat;
        const remainingPercent = (expiryTime - currentTime) / totalTime;

        if (remainingPercent < (1 - TOKEN_REFRESH_THRESHOLD) &&
            Date.now() - lastTokenRefresh > MIN_REFRESH_INTERVAL) {
            logger.info('checkAndRefreshToken: Token süresi azaldı, otomatik yenileniyor...', { remainingPercent: (remainingPercent * 100).toFixed(2) });
            return await authService.refreshToken();
        }

        return true; // Token hala geçerli
    } catch (error) {
        logger.error('Token decode veya kontrol hatası:', error);
        if (Date.now() - lastTokenRefresh > MIN_REFRESH_INTERVAL) {
            return await authService.refreshToken();
        }
        return false;
    }
};

// Store'a direkt erişim olmadığı için getState fonksiyonunu tanımla
let getState = null;

export const setStoreGetter = (getter) => {
    getState = getter;
};

// Interceptor'ları ayarla - Dosya yüklendiğinde hemen çalışsın
const setupInterceptors = () => {

    // Request interceptor - istekleri göndermeden önce token ve tenant subdomain ekle
    api.interceptors.request.use(
        async (config) => {
            // Tenant subdomain - Eğer manuel geçilmişse onu kullan, yoksa hostname'den çöz
            const sub = config.subdomain || getTenantSubdomain();
            if (sub && sub !== 'app' && sub !== 'admin') {
                config.headers['X-Tenant-Subdomain'] = sub;
            }

            // KC modunda: token'ı KC instance'tan al, gerekirse yenile
            let token = await ensureKcToken(30);

            if (!token) {
                // Legacy (non-KC) flow: memory'deki (Zustand) access token'ı kontrol et,
                // süresi azaldıysa httpOnly refresh cookie ile yenile.
                await checkAndRefreshToken();
                token = getState ? getState().token : null;
            }

            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            } else {
                // Login, refresh gibi isteklerde token olmaması normaldir
                const isAuthPath = config.url?.includes('auth/login') || config.url?.includes('auth/refresh');
                if (!isAuthPath) {
                    console.warn('Token bulunamadı, istek token olmadan gönderiliyor:', config.url);
                }
            }

            if (process.env.NODE_ENV === 'development') {
                console.debug(`[API Request] ${config.method?.toUpperCase()} ${config.url}`, {
                    baseURL: config.baseURL
                });
            }

            return config;
        },
        (error) => {
            return Promise.reject(error);
        }
    );

    // Response interceptor - 401 hataları için token yenileme
    api.interceptors.response.use(
        (response) => response,
        async (error) => {
            const originalRequest = error.config;

            // Token süresi dolduysa ve henüz yenileme denemesi yapılmadıysa
            if (error.response?.status === 401 && !originalRequest._retry) {
                // Son yenileme işleminden beri yeterli zaman geçtiyse yenile
                const now = Date.now();
                if (now - lastTokenRefresh < MIN_REFRESH_INTERVAL) {
                    await new Promise(resolve => setTimeout(resolve, MIN_REFRESH_INTERVAL));
                }

                // Eğer token zaten yenileniyorsa, bu isteği bekleme kuyruğuna ekle
                if (isRefreshing) {
                    return new Promise((resolve) => {
                        addRefreshSubscriber((token) => {
                            originalRequest.headers.Authorization = 'Bearer ' + token;
                            resolve(api(originalRequest));
                        });
                    });
                }

                originalRequest._retry = true;
                isRefreshing = true;

                try {
                    if (getState) {
                        const refreshFn = getState().refreshToken;
                        const refreshSuccess = await refreshFn();

                        if (refreshSuccess) {
                            // Yeni token ile isteği tekrarla
                            const { token } = getState();
                            originalRequest.headers.Authorization = `Bearer ${token}`;
                            onTokenRefreshed(token);
                            isRefreshing = false;
                            lastTokenRefresh = Date.now();
                            return api(originalRequest);
                        }
                        isRefreshing = false;
                        // FE-6: aktif çağrı sırasında oturumu kapatıp sayfadan atma — görüşme kopmasın.
                        // App.js'in setStoreGetter ile sağladığı getState() sadece {token,user,refreshToken}
                        // içerir (callStatus yok) — bu yüzden tam store'a doğrudan useStore.getState() ile bakılıyor.
                        // Çağrı bittiğinde sonraki 401'de normal clearAuthData akışı devam eder.
                        if (useStore.getState().callStatus !== 'connected') {
                            clearAuthData();
                        }
                        return Promise.reject(error);
                    }
                } catch (err) {
                    logger.error('Token yenileme genel hatası:', err);
                    isRefreshing = false;

                    // Genel bir hata oluştuğunda çıkış işlemi yapmak yerine
                    // sadece mevcut isteği reddet, oturumu açık tut
                    return Promise.reject(error);
                }

                isRefreshing = false;
            }

            return Promise.reject(error);
        }
    );
};

// Interceptorları hemen çalıştır
setupInterceptors();

// Oturum bilgilerini temizle - sadece bilinçli çıkış yapıldığında kullanılmalı
// Token artık sadece memory'de (Zustand) — temizlik store reset'i + backend'in
// httpOnly refresh cookie'yi silmesiyle (logout/refresh endpoint Set-Cookie expire) yapılır.
const clearAuthData = () => {
    // CI-029: the real store — App.js's getter object has no logout().
    useStore.getState().logout();

    // Eğer tarayıcı ortamındaysak login sayfasına uygun bir şekilde yönlendir
    if (typeof window !== 'undefined') {
        const currentPath = window.location.pathname;
        logger.info('clearAuthData: Oturum temizleniyor, yönlendiriliyor...', { currentPath });
        if (currentPath.startsWith('/admin')) {
            window.location.href = '/login?role=admin';
        } else if (currentPath.startsWith('/agent') || currentPath.startsWith('/customer')) {
            window.location.href = '/login?role=agent';
        } else {
            // Ana panele (HomePage) yönlendir
            window.location.href = '/';
        }
    }
};

// FE-141: KYC backend path'ini paylaşımlı `api` instance'ının baseURL'ine (API_URL, örn. '/api/')
// göre relative hale getirir — kycBaseUrl (örn. '/api/v1') ile aynı '/api' önekini paylaştığı için
// api.get() ile birlikte kullanılınca çift prefix (/api/api/v1/...) oluşmaz.
const buildKycPath = (segment) => {
    const kycBaseUrl = clientConfig.REACT_APP_KYC_BACKEND_BASE_URL;
    if (!kycBaseUrl) throw new Error('KYC Backend URL yapılandırılmamış');

    const fullPath = `${kycBaseUrl}${segment}`;
    const apiPrefix = API_URL.replace(/\/$/, '');
    const relativePath = fullPath.startsWith(apiPrefix)
        ? fullPath.slice(apiPrefix.length)
        : fullPath;

    return relativePath.replace(/^\/+/, '');
};

// Auth servisi API fonksiyonları
const authService = {
    // Login işlemi
    login: async (credentials) => {
        logger.debug('authService.login starting', { email: credentials.email, subdomain: credentials.subdomain });
        try {
            // Subdomain'i header için ayıklıyoruz ama body içinde de (loginBody) gönderiyoruz
            const { subdomain } = credentials;
            const response = await api.post('agent/login', { email: credentials.email, password: credentials.password, role: credentials.role, subdomain: subdomain || undefined });
            logger.info('authService.login success');
            return response.data;
        } catch (error) {
            logger.error('authService.login error', error);
            throw error;
        }
    },

    // Token yenileme — PLT-35: identity-service artık /auth/refresh sunmuyor (PLT-18).
    // Keycloak'un kendi updateToken() mekanizmasını kullan (bkz. keycloak.js ensureKcToken).
    refreshToken: async () => {
        try {
            const now = Date.now();
            if (now - lastTokenRefresh < MIN_REFRESH_INTERVAL) {
                await new Promise(resolve => setTimeout(resolve, MIN_REFRESH_INTERVAL));
            }

            const token = await ensureKcToken(30);
            if (token) {
                const decoded = jwtDecode(token);
                const user = {
                    id: decoded.id || decoded.sub,
                    email: decoded.email,
                    role: decoded.role,
                    tenantId: decoded.tenantId,
                    first_name: decoded.firstName || decoded.given_name || '',
                    last_name: decoded.lastName || decoded.family_name || '',
                    sid: decoded.sid
                };
                if (getState) getState().setToken(token);
                if (getState) getState().setUser(user);
                lastTokenRefresh = Date.now();
                return { token, user };
            }
            return null;
        } catch (error) {
            logger.error('refreshToken fonksiyonu hatası:', error);
            return null;
        }
    },

    // Oturumu kontrol et
    verifySession: async () => {
        logger.debug('authService.verifySession starting');
        try {
            const response = await api.get('auth/me');
            logger.debug('authService.verifySession success');
            return response.data;
        } catch (error) {
            logger.error('authService.verifySession error', error);
            throw error;
        }
    },

    // Token geçerliliğini ve süresini kontrol et
    checkTokenValidity: async () => {
        return await checkAndRefreshToken();
    },

    // Çıkış yap - kullanıcı bilinçli olarak çıkış yaptığında
    logout: async () => {
        // PLT-18: identity-service artık login/logout barındırmıyor — blacklist yazma işi
        // gateway'in kendi kc-logout endpoint'ine taşındı (bkz. CLAUDE.md "PLT-18" bölümü).
        // keycloak.logout()'tan ÖNCE çağrılır ki token KC oturumu kapanmadan blacklist'e girsin.
        // CI-029: Keycloak modunda App.js setStoreGetter'ı hiç çağırmıyor (sadece legacy AuthWrapper),
        // getState null kalıyor ve kc-logout atlanıyordu — token önce aktif KC instance'ından okunur.
        const token = getKcToken() || (getState ? getState().token : null);
        if (token) {
            // Bare axios (same base URL/credentials as `api`), not `api`: its request interceptor would
            // refresh the token or redirect to the Keycloak login mid-logout, and its 401 handler waits.
            await axios.post('auth/kc-logout', {}, {
                baseURL: api.defaults.baseURL,
                withCredentials: true,
                timeout: 10000,
                headers: { Authorization: `Bearer ${token}` }
            }).catch(() => { });
        }

        if (keycloak.authenticated || (await import('../keycloak')).keycloakAdmin.authenticated) {
            const activeKc = keycloak.authenticated ? keycloak : (await import('../keycloak')).keycloakAdmin;
            // CI-029: do NOT reset the store before this redirect. A reset makes the route guards
            // (App.js CompanyAdminRoute / AgentRoute / AdminRoute) call keycloak.login(), whose navigation
            // cancels this logout (DEV 2026-10-10: openid-connect/logout 499, then a silent re-login on the
            // still-alive SSO session). The redirect unloads the page, which discards the in-memory session.
            activeKc.logout({ redirectUri: window.location.origin + '/' });
            return true;
        }

        clearAuthData();
        return true;
    },

    // --- KYC ve Müşteri Servis Metotları ---

    /**
     * Müşteri detaylarını ve kimlik bilgilerini getirir (Communication Service)
     * @param {string} idNumber TC Kimlik Numarası
     */
    getCustomerDetail: async (idNumber) => {
        try {
            const response = await api.get(`public/customer-detail/${idNumber}`);
            return response.data;
        } catch (error) {
            logger.error(`authService.getCustomerDetail error for ${idNumber}`, error);
            throw error;
        }
    },

    /**
     * Müşterinin son yüz görüntülerini getirir (KYC Backend)
     * @param {string} idNumber TC Kimlik Numarası
     */
    getFaceImages: async (idNumber) => {
        try {
            // FE-141: 401'e neden olan ham axios.get() (Bearer eklenmiyordu) kaldırıldı,
            // Authorization header'ını otomatik ekleyen paylaşımlı `api` instance'ı kullanılıyor.
            const response = await api.get(buildKycPath(`/face/last-by-tc/${idNumber}`));
            return response.data;
        } catch (error) {
            logger.error(`authService.getFaceImages error for ${idNumber}`, error);
            throw error;
        }
    },

    /**
     * Kimlik kartı ön yüz görselini getirir
     */
    getIdCardFront: async (idNumber) => {
        try {
            const response = await api.get(buildKycPath(`/idcard/front-image/last-by-tc/${idNumber}`));
            return response.data;
        } catch (error) {
            logger.error(`authService.getIdCardFront error for ${idNumber}`, error);
            throw error;
        }
    },

    /**
     * Kimlik kartı arka yüz görselini getirir
     */
    getIdCardBack: async (idNumber) => {
        try {
            const response = await api.get(buildKycPath(`/idcard/back-image/last-by-tc/${idNumber}`));
            return response.data;
        } catch (error) {
            logger.error(`authService.getIdCardBack error for ${idNumber}`, error);
            throw error;
        }
    }
};


// Not: Periyodik token kontrolü AuthWrapper (App.js) içindeki setInterval tarafından yönetilir.
// Burada ikinci bir setInterval başlatmak eşzamanlı yenileme döngülerine yol açar — kaldırıldı.

export { api, clearAuthData, buildKycPath };
export default authService;
